/**
 * MIONGA Beverages — Premium institutional presentation platform.
 * Route: /mionga
 * Purpose: strategic presentation for governments, investors, hospitality partners.
 * NOT e-commerce. All images marked `data-asset="official"` are placeholders
 * awaiting official brand assets uploaded by the client.
 */
import React from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Coffee, Wine, GlassWater, Users, Leaf, MapPin, Building2,
  ArrowRight, Send, Sparkles, Sun, Home, Baby,
  Zap, Package, Plane, Hotel, Handshake, ZoomIn, X, Play, Pause, Volume2, VolumeX,
} from "lucide-react";
import { useMiongaContent } from "./content";
import { PartnershipForm } from "./PartnershipForm";
import { AtlanticRouteMap } from "./AtlanticRouteMap";
import { LanguageSwitcher } from "@/components/hx/LanguageSwitcher";

/* ============================================================
 * MIONGA Brand Tokens — Deep Navy · Oak · Gold · Natural Green
 * ============================================================ */
const TOK = {
  navy:  "#0B1A2E",     // Deep Navy (primary bg)
  navyDeep: "#050D1A",
  navyPanel: "#111F35",
  white: "#FBFAF6",     // Warm off-white (paper feel)
  oak:   "#8B6F4A",     // Oak Wood (mid tone)
  oakSoft: "#C8A97E",   // Light Oak
  oakDeep: "#5C4530",   // Deep Oak
  gold:  "#D4A857",     // Brushed Gold
  goldSoft: "#E8C989",
  green: "#4A7C59",     // Natural Green
  greenSoft: "#7BA88D",
  border: "rgba(212,168,87,0.14)",
  ink:   "#1A1A1A",
};

const SECTION = "relative py-24 lg:py-32 px-5 lg:px-8";
const CONTAINER = "max-w-7xl mx-auto";

/* Small building blocks -------------------------------------- */
const Eyebrow = ({ children, gold }) => (
  <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.32em]"
    style={{ color: gold ? TOK.gold : TOK.oakSoft }}>
    <span className="w-8 h-[1px]" style={{ background: gold ? TOK.gold : TOK.oakSoft }} />
    {children}
    <span className="w-8 h-[1px]" style={{ background: gold ? TOK.gold : TOK.oakSoft }} />
  </div>
);

const Title = ({ children, className = "" }) => (
  <h2 className={`font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08] ${className}`}
    style={{ color: TOK.white, fontFamily: "'Montserrat', 'Cormorant Garamond', serif" }}>
    {children}
  </h2>
);

const Card = ({ children, className = "" }) => (
  <div className={`relative rounded-lg border p-6 lg:p-8 ${className}`}
    style={{
      background: "linear-gradient(180deg, rgba(17,31,53,0.85) 0%, rgba(11,26,46,0.95) 100%)",
      borderColor: TOK.border,
      boxShadow: "0 1px 3px rgba(0,0,0,0.35), 0 12px 32px -16px rgba(212,168,87,0.15)",
    }}>
    {children}
  </div>
);

/* Official brand image — sourced from MIONGA Hospitality Ecosystem deck.
   Gold-lit frame, subtle inner sheen, brightens on hover for premium feel. */
const OfficialImage = ({
  src, alt = "MIONGA Official", ratio = "16/9", className = "",
  objectPosition = "center",
}) => (
  <div className={`group relative overflow-hidden rounded-lg ${className}`}
    style={{
      aspectRatio: ratio,
      border: `1px solid ${TOK.border}`,
      background: TOK.navyDeep,
      boxShadow: "0 24px 60px -24px rgba(0,0,0,0.6), 0 2px 0 rgba(212,168,87,0.05) inset",
    }}
    data-asset="official"
    data-testid="mionga-official-image">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover transition-all duration-[900ms] ease-out will-change-transform
                 group-hover:scale-[1.04] group-hover:brightness-110"
      style={{
        objectPosition,
        filter: "brightness(1.02) saturate(1.05)",
      }}
    />
    {/* Subtle warm tint overlay to keep tone cohesive with navy/oak palette */}
    <div className="absolute inset-0 pointer-events-none opacity-[0.10]"
      style={{
        background: `radial-gradient(120% 80% at 50% 100%, ${TOK.gold}55 0%, transparent 55%)`,
      }} />
    {/* Gold hairline on hover */}
    <div className="absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-500 opacity-0 group-hover:opacity-100"
      style={{ boxShadow: `inset 0 0 0 1px ${TOK.gold}66, 0 0 40px -6px ${TOK.gold}66` }} />
  </div>
);

/* Product bottle — transparent PNG cropped from official MIONGA poster.
   Presented on a warm cream studio backdrop so navy labels remain legible. */
const ProductBottle = ({ src, alt, tokens }) => (
  <div className="relative overflow-hidden rounded-md mb-5 group/bottle" data-testid="mionga-product-bottle"
    style={{
      aspectRatio: "3/4",
      background: "radial-gradient(120% 90% at 50% 30%, #FBFAF6 0%, #F1ECDE 65%, #E6DEC7 100%)",
      border: `1px solid ${tokens.border}`,
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -30px 60px -30px rgba(212,168,87,0.25)",
    }}>
    <img src={src} alt={alt} loading="lazy"
      className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-[900ms] ease-out group-hover/bottle:scale-[1.06]"
      style={{ filter: "drop-shadow(0 22px 24px rgba(11,26,46,0.22))" }} />
    {/* Gold glow ring on hover */}
    <div className="absolute inset-0 pointer-events-none rounded-md transition-opacity duration-500 opacity-0 group-hover/bottle:opacity-100"
      style={{ boxShadow: `inset 0 0 0 1px ${tokens.gold}88, 0 0 40px -6px ${tokens.gold}66` }} />
  </div>
);

/* Brand Applications tile — labelled crop of a real-world touchpoint (cargo, uniform, glass, etc.).
   Uniform navy frame, gold hairline on hover, corner label plate. */
const BrandTile = ({ tokens, src, label, sub, ratio = "4/3", className = "", ...rest }) => (
  <figure {...rest}
    className={`group relative rounded-lg overflow-hidden ${className}`}
    style={{
      aspectRatio: ratio,
      border: `1px solid ${tokens.border}`,
      background: tokens.navyDeep,
      boxShadow: "0 22px 60px -22px rgba(0,0,0,0.6), 0 2px 0 rgba(212,168,87,0.05) inset",
    }}>
    <img src={src} alt={label} loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-out will-change-transform group-hover:scale-[1.04] group-hover:brightness-110"
      style={{ filter: "brightness(1.02) saturate(1.04)" }} />
    {/* Corner label plate */}
    <figcaption className="absolute bottom-3 left-3 px-3 py-2 rounded-md backdrop-blur-md"
      style={{ background: "rgba(11,26,46,0.78)", border: `1px solid ${tokens.gold}55` }}>
      <div className="text-[11px] font-display font-semibold leading-none" style={{ color: tokens.white }}>{label}</div>
      <div className="text-[9px] uppercase tracking-[0.22em] mt-1" style={{ color: tokens.oakSoft }}>{sub}</div>
    </figcaption>
    {/* Gold hairline on hover */}
    <div className="absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-500 opacity-0 group-hover:opacity-100"
      style={{ boxShadow: `inset 0 0 0 1px ${tokens.gold}66, 0 0 40px -6px ${tokens.gold}66` }} />
  </figure>
);

/* Institutional Ken Burns hero video for the flagship section.
   Autoplays muted, loops seamlessly. Play/Pause + volume controls. */
const FlagshipVideo = ({ tokens, label }) => {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(true);
  const [muted, setMuted] = React.useState(true);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <div className="relative rounded-lg overflow-hidden group"
      data-testid="flagship-hero-video"
      style={{
        aspectRatio: "16/9",
        border: `1px solid ${tokens.border}`,
        background: tokens.navyDeep,
        boxShadow: "0 40px 100px -30px rgba(0,0,0,0.75), 0 2px 0 rgba(212,168,87,0.06) inset",
      }}>
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        poster="/mionga/flagship-hero-poster.jpg"
        autoPlay muted loop playsInline preload="auto"
        onLoadedMetadata={(e) => { const v = e.currentTarget; v.play().catch(() => {}); }}
        data-testid="flagship-hero-video-element">
        <source src="/mionga/flagship-hero.webm" type="video/webm" />
        <source src="/mionga/flagship-hero.mp4" type="video/mp4" />
      </video>
      {/* Cinematic vignette + navy tint to keep tone consistent with the section */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(180deg, ${tokens.navyDeep}55 0%, transparent 20%, transparent 65%, ${tokens.navyDeep}bb 100%)`,
        }} />
      {/* Numbered plate */}
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full backdrop-blur-md"
        style={{ background: "rgba(11,26,46,0.72)", border: `1px solid ${tokens.gold}55` }}>
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: tokens.gold }}>
          {label}
        </span>
      </div>
      {/* Controls (bottom-right) */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2">
        <button onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}
          data-testid="flagship-video-play"
          className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition hover:scale-105"
          style={{ background: "rgba(11,26,46,0.72)", border: `1px solid ${tokens.gold}66`, color: tokens.gold }}>
          {playing ? <Pause size={15} /> : <Play size={15} />}
        </button>
        <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
          data-testid="flagship-video-mute"
          className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition hover:scale-105"
          style={{ background: "rgba(11,26,46,0.72)", border: `1px solid ${tokens.gold}66`, color: tokens.gold }}>
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
      </div>
      {/* Gold hairline on hover */}
      <div className="absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-500 opacity-0 group-hover:opacity-100"
        style={{ boxShadow: `inset 0 0 0 1px ${tokens.gold}66, 0 0 60px -6px ${tokens.gold}55` }} />
    </div>
  );
};


/* Flagship editorial shot — labelled image tile with click-to-zoom lightbox.
   Numbered plate top-left, caption bar bottom, gold hover glow. */
const FlagshipShot = ({ src, label, caption, ratio = "16/9", tokens, ...rest }) => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      <figure {...rest}
        onClick={() => setOpen(true)}
        className="group relative rounded-lg overflow-hidden cursor-zoom-in"
        style={{
          aspectRatio: ratio,
          border: `1px solid ${tokens.border}`,
          background: tokens.navyDeep,
          boxShadow: "0 30px 80px -30px rgba(0,0,0,0.7), 0 2px 0 rgba(212,168,87,0.06) inset",
        }}>
        <img src={src} alt={caption} loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-out will-change-transform group-hover:scale-[1.035] group-hover:brightness-110"
          style={{ filter: "brightness(1.02) saturate(1.06)" }} />
        {/* Numbered plate (top-left) */}
        <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full backdrop-blur-md"
          style={{
            background: "rgba(11,26,46,0.72)",
            border: `1px solid ${tokens.gold}55`,
          }}>
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: tokens.gold }}>
            {label}
          </span>
        </div>
        {/* Caption bar (bottom) */}
        <figcaption className="absolute inset-x-0 bottom-0 px-5 py-4 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, transparent 0%, rgba(11,26,46,0.78) 65%, rgba(11,26,46,0.94) 100%)",
          }}>
          <div className="text-[12.5px] leading-relaxed" style={{ color: tokens.white }}>{caption}</div>
        </figcaption>
        {/* Zoom hint icon */}
        <div className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: "rgba(11,26,46,0.72)", border: `1px solid ${tokens.gold}66` }}>
          <ZoomIn size={15} style={{ color: tokens.gold }} />
        </div>
        {/* Gold hairline on hover */}
        <div className="absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-500 opacity-0 group-hover:opacity-100"
          style={{ boxShadow: `inset 0 0 0 1px ${tokens.gold}66, 0 0 50px -6px ${tokens.gold}55` }} />
      </figure>

      {/* Lightbox */}
      {open && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-10"
          style={{ background: "rgba(4,9,17,0.92)", backdropFilter: "blur(14px)" }}
          onClick={() => setOpen(false)}
          data-testid="flagship-lightbox">
          <button
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-110"
            style={{ background: `${tokens.gold}22`, border: `1px solid ${tokens.gold}66`, color: tokens.gold }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            data-testid="flagship-lightbox-close"
            aria-label="Close">
            <X size={18} />
          </button>
          <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={caption}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              style={{ boxShadow: `0 40px 100px -20px ${tokens.gold}33` }} />
            <figcaption className="mt-4 flex items-center justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: tokens.gold }}>{label}</div>
                <div className="text-[14px] mt-1.5" style={{ color: tokens.white }}>{caption}</div>
              </div>
            </figcaption>
          </figure>
        </div>,
        document.body,
      )}
    </>
  );
};


/* ============================================================
 *  MAIN PAGE
 * ============================================================ */
export default function MiongaPage() {
  const t = useMiongaContent();
  return (
    <div data-testid="mionga-page" className="min-h-screen text-white overflow-x-hidden"
      style={{ background: TOK.navy, fontFamily: "'Montserrat', system-ui, sans-serif" }}>

      {/* Minimal top bar — MIONGA institutional */}
      <header className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-xl"
        style={{ background: "rgba(11,26,46,0.85)", borderColor: TOK.border }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-md flex items-center justify-center overflow-hidden"
              style={{ background: "#FBFAF6", border: `1px solid ${TOK.border}` }}>
              <img src="/mionga/logo-icon-transparent.png" alt="MIONGA"
                className="w-full h-full object-contain p-1"
                data-testid="mionga-header-logo-icon" />
            </div>
            <div className="leading-none">
              <div className="font-display font-bold text-[15px] tracking-tight" style={{ color: TOK.white }}>MIONGA</div>
              <div className="text-[8px] uppercase tracking-[0.24em] mt-1" style={{ color: TOK.oakSoft }}>Beverages</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-8 text-[11px] uppercase tracking-[0.20em] font-medium" style={{ color: TOK.oakSoft }}>
            <a href="#vision" className="hover:text-white transition">{t.nav.vision}</a>
            <a href="#ecosystem" className="hover:text-white transition">{t.nav.ecosystem}</a>
            <a href="#products" className="hover:text-white transition">{t.nav.products}</a>
            <a href="#sustainability" className="hover:text-white transition">{t.nav.sustainability}</a>
            <a href="#partnership" className="hover:text-white transition">{t.nav.partnership}</a>
          </nav>
          <div className="hidden md:block mr-3"><LanguageSwitcher compact /></div>
          <a href="#partnership" data-testid="mionga-nav-cta"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.20em] font-semibold transition hover:translate-y-[-1px]"
            style={{ background: TOK.gold, color: TOK.navy, boxShadow: `0 8px 20px -8px ${TOK.gold}88` }}>
            <Send size={11} /> {t.nav.request}
          </a>
        </div>
      </header>

      {/* 1. HERO ================================================== */}
      <section data-testid="mionga-hero" className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Cinematic layered gradient — oak + navy warm ambience */}
          <div className="absolute inset-0"
            style={{
              background: `radial-gradient(1200px 800px at 75% 20%, ${TOK.oakDeep}55 0%, transparent 55%),
                           radial-gradient(900px 600px at 15% 80%, ${TOK.gold}18 0%, transparent 60%),
                           linear-gradient(180deg, ${TOK.navy} 0%, ${TOK.navyDeep} 100%)`,
            }} />
          {/* Subtle grain */}
          <div className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="mb-6">
              <Eyebrow gold>{t.hero.badge}</Eyebrow>
            </div>
            <h1 data-testid="mionga-hero-title"
              className="font-display font-bold tracking-tight text-5xl sm:text-6xl lg:text-[84px] leading-[0.98] mb-8"
              style={{ color: TOK.white, fontFamily: "'Cormorant Garamond', 'Playfair Display', 'Montserrat', serif" }}>
              MIONGA
              <span className="block text-[0.42em] font-medium tracking-[0.18em] mt-3 uppercase" style={{ color: TOK.gold }}>
                {t.hero.subline}
              </span>
            </h1>
            <p className="text-lg lg:text-xl leading-relaxed max-w-lg mb-6 italic font-light" style={{ color: TOK.oakSoft }}>
              {t.hero.slogan}
            </p>
            <div className="flex items-center gap-3 mb-10">
              <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: TOK.oakSoft }}>
                Powered by
              </span>
              <img src="/mionga/moin-ola-logo.png" alt="Moin & Olá! Coffee & Connection"
                className="h-10 sm:h-11 w-auto object-contain"
                data-testid="mionga-hero-moin-logo"
                style={{ filter: "brightness(1.05) drop-shadow(0 6px 20px rgba(212,168,87,0.25))" }} />
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#vision" data-testid="mionga-cta-ecosystem"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold transition hover:translate-y-[-1px]"
                style={{ background: TOK.gold, color: TOK.navy, boxShadow: `0 12px 30px -10px ${TOK.gold}88` }}>
                <Sparkles size={13} /> {t.hero.cta1}
              </a>
              <a href="#partnership" data-testid="mionga-cta-partnership"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold border transition hover:translate-y-[-1px]"
                style={{ borderColor: TOK.gold, color: TOK.white, background: "rgba(212,168,87,0.05)" }}>
                <Send size={13} /> {t.hero.cta2} <ArrowRight size={12} />
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }}>
            <OfficialImage
              src="/mionga/hero-bottles-crop.jpg"
              alt="MIONGA Still & Sparkling — Alpine Source"
              ratio="4/5"
              className="max-w-md ml-auto"
              objectPosition="center"
            />
          </motion.div>
        </div>
      </section>

      {/* 2. VISION ================================================ */}
      <section id="vision" data-testid="mionga-vision" className={SECTION} style={{ background: TOK.navyDeep }}>
        <div className={CONTAINER}>
          <div className="text-center mb-16">
            <Eyebrow>{t.vision.eyebrow}</Eyebrow>
            <Title className="mt-6 max-w-3xl mx-auto">Building a New <span style={{ color: TOK.gold }}>Lifestyle Ecosystem.</span></Title>
            <p className="mt-6 max-w-2xl mx-auto text-[15px] leading-relaxed" style={{ color: TOK.oakSoft }}>
              {t.vision.lede}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Wine,   label: t.vision.pillars[0] },
              { icon: Coffee, label: t.vision.pillars[1] },
              { icon: Home,   label: t.vision.pillars[2] },
              { icon: Baby,   label: t.vision.pillars[3] },
              { icon: Leaf,   label: t.vision.pillars[4] },
            ].map((p, i) => (
              <motion.div key={p.label}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <Card className="text-center hover:border-[--gold]" >
                  <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${TOK.gold}22, ${TOK.oak}22)`, color: TOK.gold }}>
                    <p.icon size={20} strokeWidth={1.6} />
                  </div>
                  <div className="font-display font-semibold text-[14px] tracking-tight" style={{ color: TOK.white }}>{p.label}</div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. ECOSYSTEM MAP ========================================= */}
      <section id="ecosystem" data-testid="mionga-ecosystem" className={SECTION}>
        <div className={CONTAINER}>
          <div className="text-center mb-16">
            <Eyebrow gold>{t.ecosystem.eyebrow}</Eyebrow>
            <Title className="mt-6">{t.ecosystem.title}</Title>
          </div>

          {/* Center + 3 branches */}
          <div className="relative">
            {/* CENTER */}
            <div className="flex justify-center mb-10">
              <div className="text-center px-10 py-6 rounded-lg border-2" style={{ borderColor: TOK.gold, background: "#FBFAF6" }}>
                <div className="text-[9px] uppercase tracking-[0.32em] font-semibold mb-3" style={{ color: TOK.oakDeep }}>{t.ecosystem.center}</div>
                <img src="/mionga/logo-transparent.png" alt="MIONGA Beverages"
                  className="h-16 sm:h-20 w-auto mx-auto object-contain"
                  data-testid="mionga-ecosystem-logo" />
              </div>
            </div>

            {/* 3 branches */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { title: t.ecosystem.branches[0].title, items: t.ecosystem.branches[0].items, icon: Coffee },
                { title: t.ecosystem.branches[1].title, items: t.ecosystem.branches[1].items, icon: GlassWater },
                { title: t.ecosystem.branches[2].title, items: t.ecosystem.branches[2].items, icon: Wine },
              ].map((b, i) => (
                <Card key={b.title}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${TOK.gold}18`, color: TOK.gold }}>
                      <b.icon size={17} strokeWidth={1.6} />
                    </div>
                    <div className="font-display font-semibold text-[15px] tracking-tight" style={{ color: TOK.white }}>{b.title}</div>
                  </div>
                  <ul className="space-y-1.5">
                    {b.items.map(it => (
                      <li key={it} className="flex items-center gap-2 text-[13px]" style={{ color: TOK.oakSoft }}>
                        <span className="w-1 h-1 rounded-full" style={{ background: TOK.gold }} />{it}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. FLAGSHIP EXPERIENCE ================================== */}
      <section data-testid="mionga-flagship" className={SECTION} style={{ background: TOK.navyDeep }}>
        <div className={CONTAINER}>
          {/* Header + editorial two-column intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-10">
            <div className="lg:col-span-5">
              <Eyebrow>{t.flagship.eyebrow}</Eyebrow>
              <Title className="mt-6">{t.flagship.title}<br/><span style={{ color: TOK.gold }}>{t.flagship.accent}</span></Title>
              <p className="mt-6 text-[13.5px] leading-relaxed max-w-md" style={{ color: TOK.oakSoft }}>
                {t.flagship.gallery.lede}
              </p>
            </div>
            <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5 self-center">
              {t.flagship.items.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[14px]" style={{ color: TOK.white }}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TOK.gold }} />{f}
                </li>
              ))}
            </ul>
          </div>

          {/* Institutional hero video — Ken Burns of the flagship */}
          <FlagshipVideo tokens={TOK} label={t.flagship.gallery.title || "Inside the flagship"} />

          {/* Two secondary shots — bar + outdoor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <FlagshipShot
              src="/mionga/cafe-bar-lounge.jpg"
              label={t.flagship.gallery.shots[1].label}
              caption={t.flagship.gallery.shots[1].caption}
              ratio="4/3"
              tokens={TOK}
              data-testid="flagship-shot-bar"
            />
            <FlagshipShot
              src="/mionga/cafe-outdoor-signage.jpg"
              label={t.flagship.gallery.shots[2].label}
              caption={t.flagship.gallery.shots[2].caption}
              ratio="4/3"
              tokens={TOK}
              data-testid="flagship-shot-outdoor"
            />
          </div>
        </div>
      </section>

      {/* 5. PRODUCTS ============================================= */}
      <section id="products" data-testid="mionga-products" className={SECTION}>
        <div className={CONTAINER}>
          <div className="text-center mb-14">
            <Eyebrow gold>{t.products.eyebrow}</Eyebrow>
            <Title className="mt-6">{t.products.title} <span style={{ color: TOK.gold }}>{t.products.accent}</span></Title>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {[
              { name: t.products.items[0].name, sub: t.products.items[0].sub, img: "/mionga/product-water.png" },
              { name: t.products.items[1].name, sub: t.products.items[1].sub, img: "/mionga/product-sparkling.png" },
              { name: t.products.items[2].name, sub: t.products.items[2].sub, img: "/mionga/product-juice.png" },
              { name: t.products.items[3].name, sub: t.products.items[3].sub, img: "/mionga/product-icedtea.png" },
              { name: t.products.items[4].name, sub: t.products.items[4].sub, img: "/mionga/product-coffee.png" },
            ].map((prod, i) => (
              <motion.div key={prod.name}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.06 }}>
                <Card className="hover:translate-y-[-2px] transition-all group">
                  <ProductBottle src={prod.img} alt={prod.name} tokens={TOK} />
                  <div className="font-display font-semibold text-[15px] tracking-tight mb-1.5 text-center" style={{ color: TOK.white }}>{prod.name}</div>
                  <div className="text-[11.5px] leading-relaxed text-center" style={{ color: TOK.oakSoft }}>{prod.sub}</div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAMILY EXPERIENCE ==================================== */}
      <section data-testid="mionga-family" className={SECTION} style={{ background: TOK.navyDeep }}>
        <div className={CONTAINER}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <OfficialImage
              src="/mionga/family-kinderecke.jpg"
              alt="The Kinderecke — Family-Inclusive Premium Space"
              ratio="4/3"
            />
            <div>
              <Eyebrow>{t.family.eyebrow}</Eyebrow>
              <Title className="mt-6">{t.family.title}<br/><span style={{ color: TOK.gold }}>{t.family.accent}</span></Title>
              <ul className="mt-8 space-y-3">
                {t.family.items.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-[15px]" style={{ color: TOK.white }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: TOK.gold }} />{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. SUSTAINABILITY ======================================= */}
      <section id="sustainability" data-testid="mionga-sustainability" className={SECTION}>
        <div className={CONTAINER}>
          <div className="text-center mb-14">
            <Eyebrow>{t.sustainability.eyebrow}</Eyebrow>
            <Title className="mt-6">{t.sustainability.title} <span style={{ color: TOK.green }}>{t.sustainability.accent}</span></Title>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Zap,     title: t.sustainability.items[0].title, body: t.sustainability.items[0].body },
              { icon: Package, title: t.sustainability.items[1].title, body: t.sustainability.items[1].body },
              { icon: Leaf,    title: t.sustainability.items[2].title, body: t.sustainability.items[2].body },
              { icon: Home,    title: t.sustainability.items[3].title, body: t.sustainability.items[3].body },
            ].map((s) => (
              <Card key={s.title}>
                <div className="w-11 h-11 rounded-md flex items-center justify-center mb-4"
                  style={{ background: `${TOK.green}22`, color: TOK.greenSoft }}>
                  <s.icon size={19} strokeWidth={1.6} />
                </div>
                <div className="font-display font-semibold text-[15px] mb-2" style={{ color: TOK.white }}>{s.title}</div>
                <div className="text-[12.5px] leading-relaxed" style={{ color: TOK.oakSoft }}>{s.body}</div>
              </Card>
            ))}
          </div>

          <div className="mt-10">
            <OfficialImage
              src="/mionga/sustainability-van.jpg"
              alt="Zero-Emission Logistics — ID. Buzz Electric Fleet"
              ratio="21/9"
            />
          </div>
        </div>
      </section>

      {/* 7b. BRAND APPLICATIONS ==================================== */}
      <section data-testid="mionga-applications" className={SECTION} style={{ background: TOK.navy }}>
        <div className={CONTAINER}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-10">
            <div className="lg:col-span-6">
              <Eyebrow gold>{t.applications.eyebrow}</Eyebrow>
              <Title className="mt-6">{t.applications.title}<br/><span style={{ color: TOK.gold }}>{t.applications.accent}</span></Title>
            </div>
            <p className="lg:col-span-6 text-[13.5px] leading-relaxed self-end" style={{ color: TOK.oakSoft }}>
              {t.applications.lede}
            </p>
          </div>

          {/* Editorial grid: hero cargo tile + 4 support tiles */}
          <div className="grid grid-cols-12 gap-4">
            <BrandTile tokens={TOK} src="/mionga/brand-cargo.jpg"
              label={t.applications.items[0].label} sub={t.applications.items[0].sub}
              className="col-span-12 lg:col-span-8" ratio="21/10" data-testid="brand-tile-cargo" />
            <BrandTile tokens={TOK} src="/mionga/brand-uniform.jpg"
              label={t.applications.items[1].label} sub={t.applications.items[1].sub}
              className="col-span-6 lg:col-span-4" ratio="4/3" data-testid="brand-tile-uniform" />
            <BrandTile tokens={TOK} src="/mionga/brand-glass.jpg"
              label={t.applications.items[2].label} sub={t.applications.items[2].sub}
              className="col-span-6 lg:col-span-3" ratio="1/1" data-testid="brand-tile-glass" />
            <BrandTile tokens={TOK} src="/mionga/brand-bag.jpg"
              label={t.applications.items[3].label} sub={t.applications.items[3].sub}
              className="col-span-6 lg:col-span-3" ratio="1/1" data-testid="brand-tile-bag" />
            <BrandTile tokens={TOK} src="/mionga/brand-hangtag.jpg"
              label={t.applications.items[4].label} sub={t.applications.items[4].sub}
              className="col-span-12 lg:col-span-6" ratio="21/9" data-testid="brand-tile-hangtag" />
          </div>
        </div>
      </section>


      {/* 8. INTERNATIONAL CONNECTION ============================= */}
      <section data-testid="mionga-connection" className={SECTION} style={{ background: `linear-gradient(180deg, ${TOK.navyDeep} 0%, ${TOK.navy} 100%)` }}>
        <div className={CONTAINER}>
          <div className="text-center mb-14">
            <Eyebrow gold>{t.connection.eyebrow}</Eyebrow>
            <Title className="mt-6"><span style={{ color: TOK.gold }}>{t.connection.accentPrefix}</span> {t.connection.accentBody}</Title>
          </div>

          {/* Institutional map: Uetze (DE) → Praia (CV) */}
          <AtlanticRouteMap tokens={TOK} t={t.connection} />

          <Card className="max-w-4xl mx-auto mt-8 py-6 lg:py-8 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-10">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇩🇪</span>
                <div className="text-left">
                  <div className="font-display font-bold text-lg" style={{ color: TOK.white }}>{t.connection.de.name}</div>
                  <div className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: TOK.oakSoft }}>{t.connection.de.sub}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-[1px]" style={{ background: `linear-gradient(90deg, ${TOK.gold}, transparent)` }} />
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold whitespace-nowrap" style={{ color: TOK.gold }}>{t.connection.route}</div>
                <div className="w-10 h-[1px]" style={{ background: `linear-gradient(90deg, transparent, ${TOK.gold})` }} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇨🇻</span>
                <div className="text-left">
                  <div className="font-display font-bold text-lg" style={{ color: TOK.white }}>{t.connection.cv.name}</div>
                  <div className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: TOK.oakSoft }}>{t.connection.cv.sub}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* 9. BUSINESS MODEL ======================================= */}
      <section data-testid="mionga-model" className={SECTION}>
        <div className={CONTAINER}>
          <div className="text-center mb-14">
            <Eyebrow>{t.model.eyebrow}</Eyebrow>
            <Title className="mt-6">{t.model.title} <span style={{ color: TOK.gold }}>{t.model.accent}</span></Title>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { time: t.model.items[0].time, what: t.model.items[0].what, icon: Coffee },
              { time: t.model.items[1].time, what: t.model.items[1].what, icon: GlassWater },
              { time: t.model.items[2].time, what: t.model.items[2].what, icon: Sun },
              { time: t.model.items[3].time, what: t.model.items[3].what, icon: Handshake },
              { time: t.model.items[4].time, what: t.model.items[4].what, icon: Plane },
            ].map((tp, i) => (
              <Card key={tp.time}>
                <div className="text-[9px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: TOK.gold }}>{tp.time}</div>
                <tp.icon size={22} className="mb-3" style={{ color: TOK.oakSoft }} strokeWidth={1.4} />
                <div className="font-display font-semibold text-[13px] tracking-tight leading-tight" style={{ color: TOK.white }}>{tp.what}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 10. PARTNERSHIP ========================================= */}
      <section id="partnership" data-testid="mionga-partnership" className={SECTION} style={{ background: TOK.navyDeep }}>
        <div className={CONTAINER}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <Eyebrow gold>{t.partnership.eyebrow}</Eyebrow>
              <Title className="mt-6">{t.partnership.title} <span style={{ color: TOK.gold }}>{t.partnership.accent}</span></Title>
              <p className="mt-6 text-[15px] leading-relaxed max-w-md" style={{ color: TOK.oakSoft }}>
                {t.partnership.lede}
              </p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: Hotel,     label: t.partnership.targets[0] },
                { icon: Home,      label: t.partnership.targets[1] },
                { icon: Plane,     label: t.partnership.targets[2] },
                { icon: Building2, label: t.partnership.targets[3] },
                { icon: Package,   label: t.partnership.targets[4] },
                { icon: Handshake, label: t.partnership.targets[5] },
              ].map((p) => (
                <div key={p.label} className="rounded-lg border p-5 text-center transition hover:translate-y-[-2px]"
                  style={{ borderColor: TOK.border, background: "rgba(212,168,87,0.03)" }}>
                  <p.icon size={20} className="mx-auto mb-3" style={{ color: TOK.gold }} strokeWidth={1.4} />
                  <div className="text-[12px] font-medium tracking-tight" style={{ color: TOK.white }}>{p.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Cinematic partnership hero — handshake at a hotel lounge */}
          <div className="mt-12 relative rounded-lg overflow-hidden group"
            data-testid="mionga-partnership-hero"
            style={{
              aspectRatio: "21/9",
              border: `1px solid ${TOK.border}`,
              boxShadow: "0 40px 100px -30px rgba(0,0,0,0.75), 0 2px 0 rgba(212,168,87,0.06) inset",
            }}>
            <img src="/mionga/partnership-alliance.jpg"
              alt={`${t.partnership.title} ${t.partnership.accent}`}
              loading="lazy"
              className="w-full h-full object-cover transition-all duration-[900ms] ease-out group-hover:scale-[1.03] group-hover:brightness-110"
              style={{ filter: "brightness(1.02) saturate(1.04)" }} />
            {/* Cinematic vignette + navy tint bottom */}
            <div className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(180deg, transparent 0%, transparent 55%, ${TOK.navyDeep}cc 100%)`,
              }} />
            {/* Corner label */}
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full backdrop-blur-md"
              style={{ background: "rgba(11,26,46,0.72)", border: `1px solid ${TOK.gold}55` }}>
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: TOK.gold }}>
                {t.partnership.eyebrow}
              </span>
            </div>
            {/* Gold hairline on hover */}
            <div className="absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-500 opacity-0 group-hover:opacity-100"
              style={{ boxShadow: `inset 0 0 0 1px ${TOK.gold}66, 0 0 60px -6px ${TOK.gold}55` }} />
          </div>

          <div className="mt-10">
            <PartnershipForm tokens={TOK} t={t.partnership.form} />
          </div>
        </div>
      </section>

      {/* 11. FINAL MESSAGE ======================================= */}
      <section data-testid="mionga-closing" className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0"
          style={{
            background: `radial-gradient(800px 500px at 50% 50%, ${TOK.gold}18 0%, transparent 60%),
                         linear-gradient(180deg, ${TOK.navy}, ${TOK.navyDeep})`,
          }} />
        <div className="relative max-w-4xl mx-auto text-center">
          {/* Official brand seal — logo on a cream badge, gold hairline */}
          <div className="inline-block rounded-lg px-10 py-6 mb-10"
            style={{
              background: "#FBFAF6",
              border: `1px solid ${TOK.gold}66`,
              boxShadow: `0 30px 80px -30px ${TOK.gold}55, 0 2px 0 rgba(255,255,255,0.4) inset`,
            }}
            data-testid="mionga-closing-logo">
            <img src="/mionga/logo-transparent.png" alt="MIONGA Beverages"
              className="h-20 sm:h-24 w-auto object-contain mx-auto" />
          </div>

          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-8"
            style={{ color: TOK.white, fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}>
            {t.closing.brand} <span style={{ color: TOK.gold }}>{t.closing.accent}</span>
          </h2>
          <p className="text-lg lg:text-xl leading-relaxed italic max-w-2xl mx-auto font-light" style={{ color: TOK.oakSoft }}>
            {t.closing.lede}
          </p>
          <div className="mt-10 pt-10 border-t inline-flex flex-col items-center px-16" style={{ borderColor: TOK.border }}>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold mb-4" style={{ color: TOK.oakSoft }}>{t.closing.poweredBy}</div>
            <img src="/mionga/moin-ola-logo.png" alt="Moin & Olá! · Coffee & Connection"
              className="h-16 sm:h-20 w-auto object-contain"
              data-testid="mionga-closing-moin-logo"
              style={{ filter: "drop-shadow(0 8px 24px rgba(212,168,87,0.35))" }} />
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="border-t py-6 px-6" style={{ borderColor: TOK.border, background: TOK.navyDeep }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4 text-[10px] uppercase tracking-[0.24em]" style={{ color: TOK.oakSoft }}>
          <div className="flex items-center gap-2.5">
            <img src="/mionga/logo-icon-transparent.png" alt="MIONGA"
              className="h-6 w-auto object-contain opacity-95"
              data-testid="mionga-footer-logo" />
            <span>{t.footer.copy}</span>
          </div>
          <div className="flex items-center gap-1.5"><MapPin size={10} /> Uetze · Praia · CPLP</div>
          <Link to="/" className="hover:text-white transition">{t.footer.back}</Link>
        </div>
      </footer>
    </div>
  );
}
